/**
 * The theme reveal.
 *
 * Changing theme repaints every custom property at once. Cross-fading that is
 * fine, but wiping the new palette in from the point you clicked is what makes
 * it feel like the click caused it.
 *
 * Built on view transitions, which snapshot the old and new frames for us. No
 * support (or reduced motion) means the change simply happens - the callback
 * runs either way, so a theme is never left half-applied.
 */

type Doc = Document & {
  startViewTransition?: (cb: () => void | Promise<void>) => { ready: Promise<void>; finished: Promise<void> };
};

export function revealFrom(x: number, y: number, apply: () => void): void {
  const doc = document as Doc;

  if (!doc.startViewTransition || document.body.classList.contains('reduce-motion')) {
    apply();
    return;
  }

  // The circle has to reach the furthest corner, or the old theme stays
  // visible in whichever corner is furthest from the click.
  const radius = Math.hypot(
    Math.max(x, window.innerWidth - x),
    Math.max(y, window.innerHeight - y)
  );

  const transition = doc.startViewTransition(apply);

  void transition.ready
    .then(() => {
      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${radius}px at ${x}px ${y}px)`
          ]
        },
        {
          duration: 620,
          easing: 'cubic-bezier(.16, 1, .3, 1)',
          // Only the incoming frame is clipped; the old one stays put
          // underneath and is revealed away rather than moving.
          pseudoElement: '::view-transition-new(root)'
        }
      );
    })
    .catch(() => { /* the change already applied; the flourish is optional */ });
}
